import { getFirstReferralCode } from "@/cart/referralCode";
import { syncReferralRewardWithRollback } from "@/cart/maticReferralReward";
import type { CartStateGetSet } from "@/cart/types";
import GRAPHQL_CLIENT from "@/graphql/client";
import type { Cart } from "@/graphql/storefront/generated";
import posthog from "posthog-js";
import { isMaticFamilyVariantId } from "@/utils/maticFamily";
import { syncPerMaticAddOnsToMatics } from "@/cart/syncPerMaticAddOnsToMatics";
import { raiseAddOnsWithMatics } from "@/cart/raiseAddOnsWithMatics";

export const updateItem = ({ get, set }: CartStateGetSet) => {
  const syncReward = syncReferralRewardWithRollback({ get, set });

  return async (lineId: string, quantity: number) => {
    const { cartId, cart: currentCart, referralCode } = get();
    if (!cartId) {
      console.error("updateItem: no cart ID");
      return;
    }

    if (quantity <= 0) {
      console.error("updateItem: quantity is less than or equal to 0");
      return;
    }

    const cartLine = currentCart?.lines.edges.find(
      ({ node }) => node.id === lineId,
    );
    const variantId = cartLine?.node.merchandise.id;

    posthog.capture("update_item", {
      lineId,
      variantId,
      quantity,
      cartId,
    });

    const { cartLinesUpdate } = await GRAPHQL_CLIENT.UpdateCartLine({
      cartId,
      lines: [{ id: lineId, quantity }],
    });

    const cart = cartLinesUpdate?.cart as Cart | undefined;

    if (!cart) {
      throw new Error("updateItem: updating line failed");
    }

    if (isMaticFamilyVariantId(variantId)) {
      const addedMatics = quantity - (cartLine?.node.quantity ?? 0);
      let nextCart = cart;

      if (addedMatics > 0) {
        try {
          nextCart = await raiseAddOnsWithMatics(nextCart, addedMatics);
        } catch (error) {
          console.error(
            "Failed to raise add-ons after Matic increment:",
            error,
          );
        }
      }

      try {
        nextCart = await syncPerMaticAddOnsToMatics(nextCart);
      } catch (error) {
        console.error("Failed to sync add-ons after Matic update:", error);
      }

      await syncReward(currentCart, nextCart);
      return;
    }

    set({
      cartId: cart.id,
      cart,
      referralCode: getFirstReferralCode(cart) || referralCode,
    });
  };
};
